import React, { useState, useCallback, useMemo } from 'react'
import { View, TouchableOpacity } from 'react-native'
import {
  Button,
  Modal,
  Portal,
  Text,
  ActivityIndicator,
  List,
  Divider,
} from 'react-native-paper'
import { PaginatedFlatList } from '../../../@ui/containers/lists/paginated-flat.list'
import { useTranslation } from 'react-i18next'
import {
  useMyTasksQuery,
  useInviteCollaboratorsByTaskMutation,
  MyTasksQuery,
} from '../../../generated/graphql'
import { MelddTasks } from '../task.types'

type TaskWithContributors = NonNullable<
  NonNullable<MyTasksQuery['myTasks']>['results']
>[number]
import Toast from 'react-native-root-toast'
import { useApolloClient } from '@apollo/client'
import { GetTaskContributionsDocument } from '../../../generated/graphql'
import * as Sentry from '@sentry/react-native'
import { createStyle } from '../../../contexts/global-styles.provider'

type ImportCollaboratorsFromTaskButtonProps = {
  targetTask: MelddTasks
}

export const ImportCollaboratorsFromTaskButton: React.FC<
  ImportCollaboratorsFromTaskButtonProps
> = ({ targetTask }) => {
  const { t } = useTranslation('tasks')
  const styles = useStyle()
  const [visible, setVisible] = useState(false)
  const client = useApolloClient()
  const limit = 20

  const { data, loading, refetch, fetchMore } = useMyTasksQuery({
    variables: { offset: 0, size: limit },
    skip: !visible,
  })

  const [inviteCollaborators, { loading: inviting }] =
    useInviteCollaboratorsByTaskMutation()

  const allTasks = data?.myTasks.results || []
  const backendTotal = data?.myTasks.count || 0

  // Filter out current task and ensure last first ordering
  const tasks = useMemo(
    () =>
      allTasks
        .filter((item) => item.task.id !== targetTask.id)
        .sort((a, b) => {
          const dateA = new Date(a.task.createdAt).getTime()
          const dateB = new Date(b.task.createdAt).getTime()
          return dateB - dateA // Most recent first
        }),
    [allTasks, targetTask.id]
  )

  // Adjust total count if current task was filtered out
  const total = useMemo(() => {
    const currentTaskIncluded = allTasks.some(
      (item) => item.task.id === targetTask.id
    )
    return currentTaskIncluded ? backendTotal - 1 : backendTotal
  }, [backendTotal, allTasks, targetTask.id])

  const handleOpen = useCallback(() => {
    setVisible(true)
    refetch()
  }, [refetch])

  const handleClose = useCallback(() => {
    setVisible(false)
  }, [])

  const handleLoadMore = useCallback(
    async ({ offset, limit: loadLimit }: { offset: number; limit: number }) => {
      if (loading) return

      await fetchMore({
        variables: {
          offset,
          size: loadLimit,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.myTasks) return prev
          return {
            myTasks: {
              ...fetchMoreResult.myTasks,
              results: [
                ...prev.myTasks.results,
                ...fetchMoreResult.myTasks.results,
              ],
            },
          }
        },
      })
    },
    [loading, fetchMore]
  )

  const handleRefresh = useCallback(async () => {
    await refetch()
  }, [refetch])

  const handleSelectTask = useCallback(
    async (sourceTaskId: string) => {
      try {
        const result = await inviteCollaborators({
          variables: {
            input: {
              sourceTaskId,
              targetTaskId: targetTask.id,
            },
          },
        })

        const invitedCount = result.data?.inviteCollaboratorsByTask?.length || 0
        if (invitedCount > 0) {
          Toast.show(t('collaborators-imported', { count: invitedCount }))
        } else {
          Toast.show(t('no-collaborators-to-import'))
        }

        await client.refetchQueries({
          include: [GetTaskContributionsDocument],
        })
        handleClose()
      } catch (e) {
        Toast.show((e as Error).message)
        console.error(e)
        Sentry.captureException(e)
      }
    },
    [inviteCollaborators, targetTask.id, client, t, handleClose]
  )

  return (
    <>
      <Button mode="outlined" onPress={handleOpen} style={styles.button}>
        {t('import-from-other-task')}
      </Button>
      <Portal>
        <Modal
          visible={visible}
          onDismiss={handleClose}
          contentContainerStyle={styles.modalContainer}
        >
          <View style={styles.modalContent}>
            <Text variant="titleLarge" style={styles.modalTitle}>
              {t('select-task-to-import')}
            </Text>
            <PaginatedFlatList
              data={tasks}
              total={total}
              initialLimit={limit}
              style={styles.list}
              contentContainerStyle={styles.listContent}
              onLoadMore={handleLoadMore}
              onRefresh={handleRefresh}
              keyExtractor={(item: unknown) => {
                const taskItem = item as TaskWithContributors
                return taskItem.task.id
              }}
              ItemSeparatorComponent={() => (
                <Divider style={styles.separator} />
              )}
              emptyMessage={t('no-tasks-found')}
              renderItem={({ item }: { item: unknown }) => {
                const taskItem = item as TaskWithContributors
                const contributorCount = taskItem.contributors.length

                const contributorNames =
                  contributorCount === 0
                    ? ''
                    : taskItem.contributors
                        .map((contributor) => {
                          const user = contributor.user
                          if (user.firstName && user.lastName) {
                            return `${user.firstName} ${user.lastName}`
                          }
                          return user.username || ''
                        })
                        .filter(Boolean)
                        .join(', ')

                return (
                  <TouchableOpacity
                    onPress={() => handleSelectTask(taskItem.task.id)}
                    disabled={inviting}
                    activeOpacity={inviting ? 1 : 0.7}
                  >
                    <List.Item
                      title={taskItem.task.title}
                      titleStyle={[
                        styles.listItemTitle,
                        inviting && styles.listItemTitleDisabled,
                      ]}
                      description={contributorNames || t('no-contributors')}
                      descriptionStyle={[
                        styles.listItemDescription,
                        inviting && styles.listItemDescriptionDisabled,
                      ]}
                      disabled={inviting}
                      style={[
                        styles.listItem,
                        inviting && styles.listItemDisabled,
                      ]}
                      right={() => {
                        if (inviting) {
                          return (
                            <ActivityIndicator
                              size="small"
                              style={styles.itemLoader}
                            />
                          )
                        }
                        if (contributorCount > 0) {
                          return (
                            <Text style={styles.contributorCount}>
                              {contributorCount}
                            </Text>
                          )
                        }
                        return null
                      }}
                    />
                  </TouchableOpacity>
                )
              }}
            />
            <Button onPress={handleClose} style={styles.closeButton}>
              {t('common:cancel')}
            </Button>
          </View>
        </Modal>
      </Portal>
    </>
  )
}

const useStyle = createStyle(({ theme }) => ({
  button: {
    marginTop: theme.spacing(1),
  },
  modalContainer: {
    backgroundColor: theme.colors.surface,
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    borderRadius: 8,
    maxHeight: '80%',
  },
  modalContent: {
    flex: 1,
    minHeight: 200,
  },
  modalTitle: {
    textAlign: 'center',
    marginBottom: theme.spacing(2),
    paddingTop: theme.spacing(3),
  },
  list: {
    flex: 1,
    maxHeight: 400,
  },
  listContent: {
    paddingBottom: theme.spacing(1),
  },
  separator: {
    marginVertical: theme.spacing(0.5),
  },
  listItem: {
    paddingVertical: theme.spacing(1),
    paddingHorizontal: theme.spacing(1),
    borderRadius: theme.roundness,
    marginVertical: theme.spacing(0.5),
  },
  listItemDisabled: {
    opacity: 0.5,
    backgroundColor: theme.colors.surfaceVariant,
  },
  listItemTitle: {
    fontSize: 16,
    fontWeight: '500',
  },
  listItemTitleDisabled: {
    opacity: 0.6,
  },
  listItemDescription: {
    fontSize: 14,
    marginTop: theme.spacing(0.5),
    opacity: 0.7,
  },
  listItemDescriptionDisabled: {
    opacity: 0.5,
  },
  contributorCount: {
    fontSize: 16,
    fontWeight: '500',
    color: theme.colors.onSurface,
    marginRight: theme.spacing(1),
  },
  contributorCountDisabled: {
    opacity: 0.6,
  },
  loader: {
    marginVertical: theme.spacing(4),
  },
  itemLoader: {
    marginRight: theme.spacing(2),
  },
  emptyText: {
    textAlign: 'center',
    padding: theme.spacing(4),
    opacity: 0.6,
  },
  closeButton: {
    marginTop: theme.spacing(2),
  },
}))
